import authRequests from '@/requests/auth/auth';
import userInfoRequests from '@/requests/app/userInfo';
import bookingRequests from '@/requests/app/booking';
import { RootState } from '@/store/index';
import { ActionTree } from 'vuex';
import {
  AuthV1LoginCreateRequest,
  AuthV1ProfilePartialUpdateRequest,
  AuthV1UserPartialUpdateRequest,
  LoginGetSuccess,
  PhoneConfirmRequestGetSuccess,
  RepeatPhoneConfirmRequestGetSuccess,
  LoginPostResponse,
  AppealConsultation,
  CatalogV1ItemListRequest,
  CatalogV1StageListRequest,
  StageImageResponse,
  CatalogItem,
  Profile,
  DocumentDownloadRequest,
  AuthV1ProfilePassportUpdateRequest,
  AuthV1ConfirmEmailRequest,
  AuthV1ProfileRegisterRequest,
  AuthV1SmsLoginRequest,
  SmsLoginResponse,
} from '@/requests/mainApi';
import {
  getCodeRequestPresenter,
  authRequestPresenter,
  checkChangedData,
  getPhoneConfirmRequestPresenter,
  getRepeatPhoneConfirmRequestPresenter,
  smsAuthRequestPresenter,
} from '@/store/presenter';
import { IResponse } from '@/requests/requestMachine';
import i18n from '@/lib/i18n';
import catalogRequests from '@/requests/app/catalog';
import notificationsRequest from '@/requests/app/notifications';
import instructionsRequest from '@/requests/app/instructions';
import { NotificationsReadBody, NotificationsRequestParams } from '@/requests/mainApi/apis/AmoNotificationsApi';
import PPDAndTPDLinks from '@/requests/app/PPDAndTPDLinks';
import { getPropertyRegisterLink } from '@/utils/helpers/helpFunctions';

export const actions: ActionTree<RootState, RootState> = {
  async getCode({ commit }, payload: any): Promise<IResponse<LoginGetSuccess>> {
    return await authRequests.getAuthCode(getCodeRequestPresenter(payload));
  },
  async getPhoneConfirmRequest({ commit }, payload: any): Promise<IResponse<PhoneConfirmRequestGetSuccess>> {	
    return await authRequests.getPhoneConfirmRequest(getPhoneConfirmRequestPresenter(payload));
  },
  async repeatPhoneConfirm({ commit }, payload: any): Promise<IResponse<RepeatPhoneConfirmRequestGetSuccess>> {	
    return await authRequests.repeatPhoneConfirm(getRepeatPhoneConfirmRequestPresenter(payload));
  },  
  async auth({ commit, dispatch }, payload: AuthV1LoginCreateRequest): Promise<IResponse<LoginPostResponse>> {
    const response = await authRequests.signIn(authRequestPresenter(payload));
    // пока звонка не было, бэк отвечает 200 без токенов; не затираем токены, полученные входом по SMS
    if (response.status && response.body?.access) {
      commit('setTemporaryAccessToken', response.body?.access);
      commit('setRefreshToken', response.body?.refresh);
    }
    return response;
  },
  // Вход по коду из SMS - запасной способ к звонку
  async smsAuth({ commit }, payload: AuthV1SmsLoginRequest): Promise<IResponse<SmsLoginResponse>> {
    const response = await authRequests.smsSignIn(smsAuthRequestPresenter(payload));
    if (response.status) {
      commit('setTemporaryAccessToken', response.body?.access);
      commit('setRefreshToken', response.body?.refresh);
    }
    return response;
  },
  async confirmEmail({ commit }, payload: AuthV1ConfirmEmailRequest) {
    const response = await authRequests.confirmEmail(payload);
    if (response.status) {
      commit('setUserData', response.body);
    }

    return response;
  },

  async setUserName({ commit }, payload: AuthV1UserPartialUpdateRequest): Promise<void> {
    await userInfoRequests.updateUserData(payload);
  },
  async getUserData({ commit, dispatch }): Promise<Profile> {
    const response = await userInfoRequests.getProfileData();
    if (response.status && response.body) {
      commit('setUserData', response.body);
      commit('setPassportData', {
        passportSeries: response.body.passportSeries,
        passportNumber: response.body.passportNumber,
        passportProviderCode: response.body.passportProviderCode,
        passportProviderDate: response.body.passportProviderDate,
        passportProvider: response.body.passportProvider,
        passportAddress: response.body.passportAddress,
        passportBirthPlace: response.body.passportBirthPlace,
      });
    }
    await dispatch('getFavouriteFlats');
    return response.body;
  },
  async getFavouriteFlats({ getters, commit, rootGetters }, payload?: CatalogV1ItemListRequest): Promise<void> {
    try {
      if (!rootGetters.userData.favourites.length) {
        commit('setFavouriteFlats', []);
        return;
      }
      const response = await catalogRequests.getFlatsInfo({
        id: rootGetters.userData.favourites,
      });

      commit('setFavouriteFlats', response.body!.results);
    } catch (e) {
      console.error(e);
    }
  },
  async updateFavouriteFlats({ getters, commit, dispatch }, payload: AuthV1ProfilePartialUpdateRequest): Promise<void> {
    const response = await userInfoRequests.updateProfileData(payload);
    if (response.status && response.body) {
      const userData = getters.userData;
      userData.favourites = response.body.favourites;
      commit('setUserData', userData);
    }
    dispatch('getFavouriteFlats');
  },
  async updateUserData(
    { getters, commit, dispatch },
    payload: AuthV1ProfilePartialUpdateRequest
  ): Promise<Profile | null> {
    if (!checkChangedData(getters['userData'], payload)) {
      dispatch('ui/addNotification', {
        message: (i18n.messages.ru.app as any).profileEdit.nonChangesDataNotification,
      });
      return null;
    }
    const response = await userInfoRequests.updateProfileData(payload);
    if (response.status && response.body) {
      commit('setUserData', response.body);
    }

    return response.body;
  },

  async registerProfile({ getters, commit, dispatch }, payload: AuthV1ProfileRegisterRequest): Promise<Profile | null> {
    const response = await userInfoRequests.registerProfileData(payload);

    return response.body;
  },

  async updatePassportData({ commit, dispatch }, payload: AuthV1ProfilePassportUpdateRequest): Promise<boolean> {
    const response = await userInfoRequests.updatePassportData(payload);
    if (response.status && response.body) {
      commit('setPassportData', response.body);
      dispatch('ui/addNotification', {
        message: (i18n.messages.ru.app as any).profileEdit.successDataNotification,
      });
    }
    return true;
  },
  signOut({ commit }) {
    commit('setAccessToken', '');
    commit('setRefreshToken', '');
  },
  async consultationRequest({ dispatch }, variables: AppealConsultation): Promise<void> {
    try {
      const result = await bookingRequests.sendConsultationRequest({
        data: variables,
      });

      if (result.status) {
        dispatch('ui/addNotification', {
          message: (i18n.messages.ru.app as any).consultation.successMessage,
        });
      }
    } catch (e) {
      console.error(e);
    }
  },
  async getConstructionStages({ commit }, payload: CatalogV1StageListRequest): Promise<StageImageResponse[]> {
    try {
      const response = await catalogRequests.getConstructionStages(payload);
      if (!response.body?.results) return [];
      return response.body.results;
    } catch (e) {
      console.error(e);
      return [];
    }
  },
  async getFlatById({ commit }, payload: CatalogV1ItemListRequest): Promise<CatalogItem[]> {
    try {
      const response = await catalogRequests.getFlatsInfo(payload);
      if (!response.body?.results) return [];
      return response.body.results;
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  async getNotifications({ commit }, params?: NotificationsRequestParams) {
    try {
      const response = await notificationsRequest.getNotifications(params);

      commit('setNotificationsCount', response.body.count);
      if (params) {
        commit('setNotifications', response.body.results);
      }
      return response.body;
    } catch (error) {
      console.error(error);
      return [];
    }
  },

  async readNotifications({ commit, state }, payload: NotificationsReadBody) {
    try {
      await notificationsRequest.readNotifications(payload);
      const setOfIds = new Set(payload.ids);
      const bufferedNotifications = state.notifications;
      const l = bufferedNotifications.length;
      for (let i = 0; i < l; i++) {
        const notification = bufferedNotifications[i];
        if (setOfIds.has(notification.id)) {
          bufferedNotifications[i].isRead = 1;
        } else {
          continue;
        }
      }

      commit('setNotifications', bufferedNotifications);
    } catch (error) {
      console.error(error);
      return [];
    }
  },

  async getPPDAndTPDLinks({ commit, state }) {
    try {
      const response = await PPDAndTPDLinks.getLinks();
      commit('setPPDAndTPDLinks', response.body);
    } catch (error) {
      console.error(error);
      return {};
    }
  },

  async getOrganization({ commit, state }) {
    try {
      const response = await PPDAndTPDLinks.getOrganization();
      commit('setOrganization', response.body);
    } catch (error) {
      console.error(error);
      return {};
    }
  },

  openRegisterWindow() {
    window.open(getPropertyRegisterLink(), '_blank', 'width=600,height=600,left=200,top=200');
  },

  async downloadObjectDocs({ commit }, payload: DocumentDownloadRequest) {
    try {
      const response = await catalogRequests.downloadObjectDocs(payload);

      const url = URL.createObjectURL(response.body);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.setAttribute('download', `${payload.name}.${payload.ext}`);
      anchor.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      return {};
    }
  },

  async getBasisWidgetToken(state, payload) {
    try {
      const response = await authRequests.getBasisWidgetToken();
      const url = payload ? `${response.body.url}${payload}` : response.body.url;

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    } catch (error) {
      console.error(error);
    }
  },

  async createMortgageOrder(state, payload) {
    try {
      const response = await bookingRequests.createMortgageOrder(payload);

      return response.body;
    } catch (error) {
      console.error(error);
      return {};
    }
  },

  async getInstructions({ commit }) {
    try {
      const response = await instructionsRequest.getInstructions();
      const instructions = response.body.sort((a, b) => a.order - b.order);
      commit('setInstructions', instructions);
      return response.body;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
};
